// React context for the resolved brand bundle (assessment.brand). Lets any
// block renderer pull the brand logo / name / link without prop-drilling
// through every layer.
//
// Wrap the block tree with `<BrandProvider brand={assessment.brand}>` once
// at the page root (App.tsx, ResultsPage, AnswersPage, SampleResultPage).
// Then any block can `const brand = useBrand()` and fall back to brand
// fields when its own props are empty — keeping authoring purely declarative.
//
// Why a context (not prop-drill or block prop): blocks are authored as JSON
// in D1. We don't want every event author to re-paste the brand logo URL
// into every block — they shouldn't have to know the brand at all. The brand
// is already resolved server-side (worker/src/brands.ts → resolveBrandForAssessment),
// so the taker just consumes it once at the top and threads it down.

import { createContext, useContext, type ReactNode } from 'react';
import type { ResolvedBrand } from '../types';

const BrandCtx = createContext<ResolvedBrand | null>(null);

export function BrandProvider({
  brand,
  children,
}: {
  brand: ResolvedBrand | null | undefined;
  children: ReactNode;
}) {
  return <BrandCtx.Provider value={brand ?? null}>{children}</BrandCtx.Provider>;
}

/** Read the resolved brand bundle for the current page. Returns null when
 *  no brand is in context (legacy path or non-event surfaces). Block
 *  renderers should treat null as "no brand fallback available". */
export function useBrand(): ResolvedBrand | null {
  return useContext(BrandCtx);
}
